import { isEmbedded } from "@/lib/embedded";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams, useSearch } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { X, ChevronLeft, ChevronRight, Flame, Sparkles, Trophy, RotateCcw, PenTool, ArrowLeft, ArrowRight, Loader2, Award, Star, Medal, Compass } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Confetti } from "@/components/Confetti";
import { Mascot } from "@/components/lesson/Mascot";
import { LessonBlockRenderer } from "@/components/lesson/LessonBlockRenderer";
import type { CheckResult } from "@/components/lesson/CheckShell";
import { isCheckBlock, type LessonBlock } from "@shared/lessonBlocks";
import { playLessonComplete } from "@/lib/sounds";
import { capturePostHogEvent } from "@/lib/posthog";

interface LessonPayload {
  lesson: {
    id: string;
    title: string;
    slug: string;
    grade: number;
    subject: string;
    topic: string;
    description: string | null;
    emoji: string | null;
    blocks: LessonBlock[];
    estimatedMinutes: number;
    xpReward: number;
    status: string;
  };
  progress: {
    status: "not_started" | "in_progress" | "completed";
    currentBlock: number;
    score: number;
    totalChecks: number;
    xpAwarded: number;
    results?: Record<string, CheckResult>;
  } | null;
  // Present only when the lesson was opened from a course (?course=<slug>).
  course?: {
    slug: string;
    title: string;
    position: number;
    total: number;
    nextLesson: { id: string; slug: string; title: string } | null;
    previousLesson: { id: string; slug: string; title: string } | null;
  } | null;
}

interface CompletionResult {
  alreadyCompleted: boolean;
  xpAwarded: number;
  baseXp: number;
  bonusXp: number;
  score: number;
  totalChecks: number;
  levelUp: boolean;
  level: { currentLevel: number; totalXP: number; levelXP: number; nextLevelXP: number } | null;
  badgeText: string | null;
  // Present only when the lesson sits in a course the student is enrolled in.
  course?: {
    slug: string;
    title: string;
    completedLessons: number;
    totalLessons: number;
    completed: boolean;
    credential: { code: string; title: string } | null;
    nextLesson: { id: string; slug: string; title: string } | null;
  } | null;
}

const STREAK_LINES = ["Nice!", "Two in a row!", "Three in a row, on fire!", "Four! Unstoppable!", "Five straight! Legend!"];

export default function LessonPlayer() {
  const { slug } = useParams<{ slug: string }>();
  const [, setLocation] = useLocation();
  const search = useSearch();
  // A course slug on the URL asks the API for course context (position, next lesson).
  const courseSlug = new URLSearchParams(search).get("course") || null;
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<LessonPayload>({
    queryKey: ["/api/lessons", slug, courseSlug],
    enabled: isAuthenticated && !!slug,
    staleTime: 60_000,
    queryFn: async () => {
      const url = `/api/lessons/${slug}${courseSlug ? `?course=${encodeURIComponent(courseSlug)}` : ""}`;
      const res = await apiRequest("GET", url);
      return res.json();
    },
  });

  const lesson = data?.lesson;
  const courseContext = data?.course ?? null;
  const blocks = lesson?.blocks ?? [];
  const [index, setIndex] = useState(0);
  const [results, setResults] = useState<Record<string, CheckResult>>({});
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set());
  const [streak, setStreak] = useState(0);
  const [finished, setFinished] = useState<CompletionResult | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const initialised = useRef(false);
  const blockStartedAt = useRef(Date.now());

  // Moving straight from one course lesson to the next keeps this component mounted,
  // so wipe the previous lesson's state before the new one initialises.
  useEffect(() => {
    initialised.current = false;
    setIndex(0);
    setResults({});
    setResolvedIds(new Set());
    setStreak(0);
    setFinished(null);
    setShowConfetti(false);
  }, [slug]);

  // Resume where the student left off (fresh replay if already completed).
  useEffect(() => {
    if (!data || initialised.current) return;
    initialised.current = true;
    const p = data.progress;
    if (p?.status === "in_progress") {
      setIndex(Math.min(p.currentBlock || 0, Math.max(0, blocks.length - 1)));
      // Restore already-answered checks so a resumed lesson does not ask them again.
      if (p.results && typeof p.results === "object") {
        setResults(p.results);
        setResolvedIds(new Set(Object.keys(p.results)));
      }
    }
    if (p?.status === "not_started" || !p) {
      capturePostHogEvent("lesson_started", { lessonId: data.lesson.id, slug: data.lesson.slug, subject: data.lesson.subject, topic: data.lesson.topic, grade: data.lesson.grade });
    }
    blockStartedAt.current = Date.now();
  }, [data]);

  useEffect(() => {
    blockStartedAt.current = Date.now();
    window.scrollTo({ top: 0 });
  }, [index]);

  const progressMutation = useMutation({
    mutationFn: async (payload: { currentBlock: number; blockId?: string; correct?: boolean; attempts?: number; timeSpent?: number }) => {
      if (!lesson) return null;
      const res = await apiRequest("POST", `/api/lessons/${lesson.id}/progress`, payload);
      return res.json();
    },
  });

  const completeMutation = useMutation({
    mutationFn: async (timeSpent: number) => {
      const res = await apiRequest("POST", `/api/lessons/${lesson!.id}/complete`, { timeSpent });
      return (await res.json()) as CompletionResult;
    },
    onSuccess: (result) => {
      setFinished(result);
      setShowConfetti(true);
      playLessonComplete();
      capturePostHogEvent("lesson_completed", { lessonId: lesson!.id, slug: lesson!.slug, subject: lesson!.subject, topic: lesson!.topic, score: result.score, totalChecks: result.totalChecks, xpAwarded: result.xpAwarded, replay: result.alreadyCompleted });
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
      queryClient.invalidateQueries({ queryKey: ["/api/lessons/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user/level"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/progress"] });
      if (courseSlug) {
        queryClient.invalidateQueries({ queryKey: ["/api/courses"] });
        queryClient.invalidateQueries({ queryKey: ["/api/me/courses"] });
        queryClient.invalidateQueries({ queryKey: ["/api/recommendations"] });
        queryClient.invalidateQueries({ queryKey: ["/api/credentials/mine"] });
      }
    },
    onError: () => toast({ title: "Could not save your result", description: "Check your connection and try again.", variant: "destructive" }),
  });

  const restartMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/lessons/${lesson!.id}/restart`);
      return res.json();
    },
    onSuccess: () => {
      setResults({});
      setResolvedIds(new Set());
      setStreak(0);
      setFinished(null);
      setIndex(0);
      queryClient.invalidateQueries({ queryKey: ["/api/lessons", slug] });
      queryClient.invalidateQueries({ queryKey: ["/api/lessons"] });
    },
  });

  const block = blocks[index];
  const total = blocks.length;
  const checkTotal = useMemo(() => blocks.filter(isCheckBlock).length, [blocks]);
  const isCheck = block ? isCheckBlock(block) : false;
  const resolved = !!block && (!isCheck || resolvedIds.has(block.id) || !!results[block.id]);
  const isLast = index >= total - 1;
  const secondsOnBlock = () => Math.max(0, Math.round((Date.now() - blockStartedAt.current) / 1000));

  const handleResult = (r: CheckResult) => {
    if (!block) return;
    setResults(prev => ({ ...prev, [block.id]: r }));
    setResolvedIds(prev => new Set(prev).add(block.id));
    setStreak(s => (r.correct ? s + 1 : 0));
    progressMutation.mutate({ currentBlock: index, blockId: block.id, correct: r.correct, attempts: r.attempts, timeSpent: secondsOnBlock() });
    blockStartedAt.current = Date.now();
  };

  const goNext = () => {
    if (!lesson) return;
    if (isLast) {
      completeMutation.mutate(secondsOnBlock());
      return;
    }
    const next = index + 1;
    progressMutation.mutate({ currentBlock: next, timeSpent: secondsOnBlock() });
    setIndex(next);
  };
  const goBack = () => setIndex(i => Math.max(0, i - 1));

  const exitPath = courseSlug
    ? `/courses/${courseSlug}`
    : lesson
      ? `/learning?subject=${encodeURIComponent(lesson.subject.toLowerCase())}&topic=${encodeURIComponent(lesson.topic.toLowerCase())}&mode=lesson`
      : "/lessons";
  const quizPath = lesson
    ? `/learning?subject=${encodeURIComponent(lesson.subject.toLowerCase())}&topic=${encodeURIComponent(lesson.topic.toLowerCase())}&mode=quiz`
    : "/learning";

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !lesson) {
    const status = (error as any)?.status;
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center space-y-4">
          <Mascot mood="oops" message={status === 403 ? "This lesson is for a different grade. Let's find one for you!" : status === 404 ? "Hmm, I can't find that lesson." : "Something went wrong loading this lesson."} />
          <Button onClick={() => setLocation("/lessons")} className="rounded-xl"><ArrowLeft className="w-4 h-4 mr-2" /> All lessons</Button>
        </div>
      </div>
    );
  }

  // ---------- Completion screen ----------
  if (finished) {
    const pct = finished.totalChecks ? Math.round((finished.score / finished.totalChecks) * 100) : 100;
    const finishedCourse = finished.course ?? null;
    const courseCredential = finishedCourse?.completed ? finishedCourse.credential : null;
    const nextInCourse = finishedCourse && !finishedCourse.completed ? finishedCourse.nextLesson : null;
    const navCourseSlug = finishedCourse?.slug ?? courseSlug;
    const mood = pct >= 80 ? "cheer" : pct >= 50 ? "proud" : "happy";
    const line = finished.alreadyCompleted
      ? "Great practice! You already earned XP for this lesson, so this round was just for fun."
      : pct >= 80 ? "Outstanding! You really know this now." : pct >= 50 ? "Good work! A little more practice and you will own it." : "You finished! Every try makes you stronger. Want to go again?";
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/10 via-chart-2/5 to-amber-100/40 dark:to-amber-900/10 p-4 md:p-8">
        <Confetti active={showConfetti} duration={3500} particleCount={90} />
        <div className="max-w-xl mx-auto space-y-5">
          <div className="text-center animate-in zoom-in-75 fade-in duration-500">
            <div className="text-7xl mb-2">{lesson.emoji || "🎉"}</div>
            <h1 className="text-3xl font-extrabold">Lesson complete!</h1>
            <p className="text-muted-foreground mt-1">{lesson.title}</p>
          </div>

          <Mascot mood={mood} message={line} />

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-white dark:bg-slate-800 border border-border p-4 text-center shadow-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground font-bold">XP earned</p>
              <p className="text-3xl font-extrabold text-primary flex items-center justify-center gap-1"><Sparkles className="w-6 h-6" /> +{finished.xpAwarded}</p>
              {!finished.alreadyCompleted && finished.bonusXp > 0 && (
                <p className="text-xs text-muted-foreground mt-1">{finished.baseXp} + {finished.bonusXp} first-try bonus</p>
              )}
            </div>
            <div className="rounded-2xl bg-white dark:bg-slate-800 border border-border p-4 text-center shadow-sm">
              <p className="text-xs uppercase tracking-wide text-muted-foreground font-bold">Checks right first try</p>
              <p className="text-3xl font-extrabold">{finished.score}<span className="text-lg text-muted-foreground">/{finished.totalChecks}</span></p>
              <p className="text-xs text-muted-foreground mt-1">{pct}%</p>
            </div>
          </div>

          {finished.badgeText && (
            <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-900/25 p-4 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-300 fill-mode-both">
              <Award className="w-9 h-9 text-amber-500" />
              <div>
                <p className="text-xs uppercase tracking-wide font-bold text-amber-700 dark:text-amber-300">Title earned</p>
                <p className="text-xl font-extrabold">{finished.badgeText}</p>
              </div>
            </div>
          )}

          {finished.levelUp && finished.level && (
            <div className="rounded-2xl bg-gradient-to-r from-primary to-chart-2 text-white p-4 flex items-center gap-3 animate-in fade-in zoom-in-95 duration-500 delay-500 fill-mode-both">
              <Trophy className="w-9 h-9" />
              <div>
                <p className="text-xs uppercase tracking-wide font-bold opacity-80">Level up!</p>
                <p className="text-xl font-extrabold">You reached Level {finished.level.currentLevel}</p>
              </div>
            </div>
          )}

          {courseCredential && (
            <div
              className="rounded-2xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-900/25 p-5 text-center space-y-3 animate-in fade-in zoom-in-95 duration-500 delay-200 fill-mode-both"
              data-testid="card-course-credential"
            >
              <Medal className="w-10 h-10 text-amber-500 mx-auto" />
              <div>
                <p className="text-xs uppercase tracking-wide font-bold text-amber-700 dark:text-amber-300">Course complete!</p>
                <p className="text-xl font-extrabold">{courseCredential.title}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  You finished every lesson in {finishedCourse?.title}. That certificate is yours.
                </p>
              </div>
              <Button
                className="rounded-xl font-bold w-full"
                onClick={() => setLocation(`/credentials/${encodeURIComponent(courseCredential.code)}`)}
                data-testid="button-lesson-view-credential"
              >
                <Medal className="w-4 h-4 mr-2" /> View credential
              </Button>
            </div>
          )}

          {nextInCourse && navCourseSlug && (
            <div
              className="rounded-2xl border border-primary/30 bg-card p-4 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-200 fill-mode-both"
              data-testid="card-course-next-lesson"
            >
              <Compass className="w-8 h-8 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs uppercase tracking-wide font-bold text-muted-foreground">
                  {finishedCourse?.title} · {finishedCourse?.completedLessons} of {finishedCourse?.totalLessons} done
                </p>
                <p className="font-extrabold leading-tight truncate">Next lesson: {nextInCourse.title}</p>
              </div>
              <Button
                className="rounded-xl font-bold flex-shrink-0"
                onClick={() => setLocation(`/lessons/${nextInCourse.slug}?course=${encodeURIComponent(navCourseSlug)}`)}
                data-testid="button-lesson-next-in-course"
              >
                Go <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}

          <div className="grid gap-2 pt-2">
            <Button size="lg" className="rounded-xl font-bold" onClick={() => setLocation(quizPath)} data-testid="button-lesson-practice-quiz">
              <PenTool className="w-4 h-4 mr-2" /> Practice with a quiz
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" className="rounded-xl" onClick={() => restartMutation.mutate()} disabled={restartMutation.isPending} data-testid="button-lesson-replay">
                <RotateCcw className="w-4 h-4 mr-2" /> Do it again
              </Button>
              <Button variant="outline" className="rounded-xl" onClick={() => setLocation(exitPath)} data-testid="button-lesson-back-topic">
                <ArrowLeft className="w-4 h-4 mr-2" /> {courseSlug ? "Back to course" : "More lessons"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Player ----------
  const pct = total ? Math.round(((index + (resolved ? 1 : 0)) / total) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-chart-2/5 to-primary/10 flex flex-col">
      {/* Top bar (sits below the site navigation, which is itself sticky at top-0 with h-14) */}
      <div className={`sticky ${isEmbedded() ? "top-0" : "top-14"} z-30 bg-background/85 backdrop-blur border-b border-border`}>
        <div className="container max-w-3xl mx-auto px-3 py-2 flex items-center gap-3">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setLocation(exitPath)} aria-label="Leave lesson" data-testid="button-lesson-exit">
            <X className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <Progress value={pct} className="h-3 rounded-full" />
          </div>
          <div className="flex items-center gap-2 text-sm font-bold">
            {streak >= 2 && (
              <span key={streak} className="flex items-center gap-1 text-orange-500 animate-bounce" title={STREAK_LINES[Math.min(streak - 1, STREAK_LINES.length - 1)]}>
                <Flame className="w-4 h-4" /> {streak}
              </span>
            )}
            <span className="text-muted-foreground tabular-nums">{index + 1}/{total}</span>
          </div>
        </div>
      </div>

      {/* Slim course bar, only when the lesson was opened from a course */}
      {courseContext && (
        <div className="bg-primary/10 border-b border-primary/20" data-testid="bar-course-context">
          <div className="container max-w-3xl mx-auto px-3 py-2 flex items-center gap-2 text-xs sm:text-sm">
            <button
              type="button"
              onClick={() => setLocation(`/courses/${courseContext.slug}`)}
              className="font-bold text-primary hover:underline truncate"
              data-testid="link-course-context"
            >
              {courseContext.title}
            </button>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground whitespace-nowrap">
              Lesson {courseContext.position} of {courseContext.total}
            </span>
          </div>
        </div>
      )}

      {/* Block */}
      <div className="flex-1 container max-w-3xl mx-auto px-3 py-4 md:py-6 pb-28">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
          <span>{lesson.emoji}</span>
          <span className="font-semibold text-foreground">{lesson.title}</span>
          <span>·</span>
          <span>{lesson.topic}</span>
          {isCheck && <span className="ml-auto flex items-center gap-1 text-primary font-semibold"><Star className="w-3.5 h-3.5" /> Check {Math.min(checkTotal, blocks.slice(0, index + 1).filter(isCheckBlock).length)} of {checkTotal}</span>}
        </div>
        {/* CSS-driven entrance (tailwindcss-animate) so content never depends on JS animation frames. */}
        <div
          key={block?.id ?? index}
          className="rounded-3xl bg-card border border-card-border shadow-sm p-5 md:p-7 animate-in fade-in slide-in-from-right-4 duration-300"
        >
          {block && (
            <LessonBlockRenderer
              key={block.id}
              block={block}
              onResult={handleResult}
              alreadyDone={results[block.id] ?? null}
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-background/90 backdrop-blur border-t border-border" style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
        <div className="container max-w-3xl mx-auto px-3 py-3 flex items-center gap-2">
          <Button variant="ghost" className="rounded-xl" onClick={goBack} disabled={index === 0} data-testid="button-lesson-prev">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex-1" />
          <Button
            size="lg"
            className={`rounded-xl px-8 font-bold text-base ${isLast && resolved ? "bg-gradient-to-r from-primary to-chart-2 text-white" : ""}`}
            disabled={!resolved || completeMutation.isPending}
            onClick={goNext}
            data-testid="button-lesson-continue"
          >
            {completeMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : isLast ? <>Finish lesson <Sparkles className="w-4 h-4 ml-2" /></> : <>Continue <ChevronRight className="w-4 h-4 ml-1" /></>}
          </Button>
        </div>
      </div>
    </div>
  );
}
